import axios from 'axios';
import _omit from 'lodash/omit';
import { captureException } from '@sentry/react';
import { isUnauthenticatedUrl } from '@app/constants/axios';
import { oktaAuth } from '@app/helpers/auth/oidc';
import navigationService from '@app/services/navigationService';
import {
  ensureBennieSession,
  exchangeBennieSession,
} from '@app/helpers/bennie-session';

const baseConfig = {
  headers: {
    'Content-Type': 'application/json',
  },
  retry: 1,
  withCredentials: true,
};

const coreClient = axios.create({
  baseURL: '/api/core',
  ...baseConfig,
});

const insightsClient = axios.create({
  baseURL: `/api/reporting`,
  ...baseConfig,
});

const cmsClient = axios.create({
  baseURL: `/api/bennie-cms`,
  ...baseConfig,
});

const uiServicesClient = axios.create({
  baseURL: `/api/ui-services`,
  ...baseConfig,
});

const marketplaceApiClient = axios.create({
  baseURL: `/api/marketplace-partners`,
  ...baseConfig,
});

const findADoctorClient = axios.create({
  baseURL: `/api/findcare/v1`,
  ...baseConfig,
});

const umsClient = axios.create({
  baseURL: `/api/usermanagement/v1/users`,
  ...baseConfig,
});

const requestInterceptor = async config => {
  const isUnauthenticated = isUnauthenticatedUrl(config.url);

  if (!isUnauthenticated) {
    // Ensure the Bennie session cookie exists before the request is sent.
    // No-op when the session is unconfigured or the signal cookie is present.
    // A failure must not block the outgoing request, so it is reported and
    // swallowed; the 401 retry path below recovers a missing/expired session.
    try {
      await ensureBennieSession(oktaAuth);
    } catch (error) {
      captureException(error, {
        tags: {
          context: 'bennie_session_exchange',
          reason: 'initial_ensure_axios',
        },
      });
    }
  }

  const accessToken = oktaAuth.getAccessToken();

  if (isUnauthenticated) {
    return {
      ...config,
      withCredentials: false,
      headers: _omit(config.headers, 'Authorization'),
    };
  }

  return {
    ...config,
    headers: {
      ...config.headers,
      Authorization: `Bearer ${accessToken}`,
    },
  };
};

const handleAuthFailure = () => {
  // Use navigation service to avoid circular dependency
  navigationService.triggerAuthFailure();
};

const errorInterceptor = client => async err => {
  const { config, response } = err;
  if (!config || !response) {
    return Promise.reject(err);
  }

  if (isUnauthenticatedUrl(config.url)) {
    // 401/403 are expected outcomes on public endpoints (e.g. bad credentials);
    // only report unexpected failures to avoid Sentry alert noise.
    if (response.status !== 401 && response.status !== 403) {
      captureException(err);
    }
    return Promise.reject(err);
  }

  const retryCodes = [401];

  // Check if this is a retry-eligible error and we have retries left
  if (config.retry && retryCodes.includes(response.status)) {
    try {
      const existingToken = await oktaAuth.tokenManager.get('accessToken');

      if (!existingToken) {
        captureException(new Error('No access token available for renewal'), {
          tags: { context: 'token_refresh_failure', reason: 'no_token' },
          extra: {
            message:
              'Token renewal attempted but no access token exists, triggering re-authentication',
          },
        });
        handleAuthFailure();
        return Promise.reject(
          new axios.Cancel('Request cancelled due to authentication failure'),
        );
      }

      const token = await oktaAuth.tokenManager
        .renew('accessToken')
        .catch(error => {
          return Promise.reject(error);
        });

      if (!token || !token.accessToken) {
        captureException(new Error('Token renewal returned invalid token'), {
          tags: { context: 'token_refresh_failure', reason: 'invalid_token' },
          extra: {
            message: 'Token renewal succeeded but returned invalid token',
          },
        });
        handleAuthFailure();
        return Promise.reject(
          new axios.Cancel('Request cancelled due to authentication failure'),
        );
      }

      config.retry = config.retry ? config.retry - 1 : 0;
      config.headers.Authorization = `Bearer ${token.accessToken}`;
      config.retries = config.retries || 1;

      // Refresh the Bennie session cookie with the renewed Okta token before
      // retrying, in case the 401 was caused by an expired session cookie.
      try {
        await exchangeBennieSession(() => Promise.resolve(token.accessToken));
      } catch (exchangeError) {
        captureException(exchangeError, {
          tags: { context: 'bennie_session_exchange', reason: 'retry_exchange' },
        });
      }

      return new Promise(resolve => {
        resolve(client({ ...config, headers: config.headers.toJSON() }));
      });
    } catch (tokenError) {
      // If token refresh fails and this is a 401, it's an auth failure
      if (response.status === 401) {
        captureException(tokenError, {
          tags: { context: 'token_refresh_failure', reason: 'renew_error' },
          extra: {
            message:
              'Token refresh failed for 401 response, redirecting to logout',
            errorMessage: tokenError?.message,
          },
        });
        handleAuthFailure();
        return Promise.reject(
          new axios.Cancel('Request cancelled due to authentication failure'),
        );
      }
      // For other errors, just continue to normal error handling
    }
  }

  if ((response.status === 401 && !config.retry) || response.status === 403) {
    handleAuthFailure();
    return Promise.reject(
      new axios.Cancel('Request cancelled due to authentication failure'),
    );
  }

  captureException(err);
  return Promise.reject(err);
};

[
  coreClient,
  insightsClient,
  cmsClient,
  uiServicesClient,
  marketplaceApiClient,
  findADoctorClient,
  umsClient,
].forEach(client => {
  client.interceptors.request.use(requestInterceptor);
  client.interceptors.response.use(null, errorInterceptor(client));
});

export {
  coreClient,
  insightsClient,
  cmsClient,
  uiServicesClient,
  marketplaceApiClient,
  findADoctorClient,
  umsClient,
};
